import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useEnv } from '../../../../../context/env-context';
import { useAuth } from '../../../../../hooks/useAuth';
import { makeRequest } from '../../../../../utils/makeRequest';
import { toast } from 'react-toastify';
import { Spinner } from '../../../../../components/Spinner';
import { useState } from 'react';
import dwollaLogo from '../../../../../assets/dwolla_logo.svg';
import tabapayLogo from '../../../../../assets/tabapay_logo.png';
import moment from 'moment';

function ConsumerBankAccountsSection({
  consumerId,
  consumerPlaidItemId,
  consumerBankAccountStatus,
  disconnectBankAccount,
  institution,
  bankAccounts,
  setBankAccounts,
  getBankAccounts,
  bankAccountOwners,
  getFundingSource,
  accountsBlockStatus,
  unblockLinkStatus,
}) {
  const [spinner, setSpinner] = useState(false);

  const { apiOriginConsumer } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const setAsMain = async plaidAccountId => {
    if (window.confirm('Are you sure?')) {
      try {
        setSpinner(true);
        const token = await getAccessTokenSilently();

        if (!token) {
          return;
        }

        const config = {
          token,
          url: `${apiOriginConsumer}/set_main_account`,
          method: 'POST',
          data: JSON.stringify({ consumerId, plaidAccountId }),
        };

        const response = await makeRequest(config);

        await getFundingSource();

        if (response === 'ok') {
          toast.success('Main account succesfuly set.');
        } else {
          toast.success(`Main account set, but funding source not created. Error: ${response}`);
        }

        setBankAccounts(prev =>
          prev.map(x =>
            x.plaidAccountId === plaidAccountId ? { ...x, isMain: true } : { ...x, isMain: false },
          ),
        );
      } catch (error) {
        toast.error(error.message);
      } finally {
        setSpinner(false);
      }
    }
  };

  const createTokens = async () => {
    try {
      setSpinner(true);
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = {
        token,
        url: `${apiOriginConsumer}/create_bank_tokens`,
        method: 'POST',
        data: JSON.stringify({ consumerId }),
      };

      await makeRequest(config);
      await getBankAccounts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  return (
    <div className="w-100">
      <Spinner visible={spinner}>
        <div>
          <div className="d-flex align-items-center gap-1 mb-1">
            <Button
              className="bo-common-button"
              disabled={
                consumerBankAccountStatus === 'NOT_CONNECTED' ||
                consumerBankAccountStatus === 'NO_TRANSFERS'
              }
              onClick={disconnectBankAccount}
            >
              <p className="bo-consumer-text-sm">Disconnect</p>
            </Button>
            <Button className="bo-common-button" onClick={createTokens}>
              <p className="bo-consumer-text-sm">Create Tokens</p>
            </Button>
          </div>
          {accountsBlockStatus.blocked && (
            <div className="d-flex align-items-end gap-2 mt-2">
              <p className="bo-consumer-text-sm text-danger text-nowrap text-truncate">
                Blocked for linking a new account until{' '}
                {moment.utc(accountsBlockStatus.blockedUntil).local().format('h:mm a')}
              </p>
              <Button className="bo-common-inline-button text-nowrap" onClick={unblockLinkStatus}>
                <p className="bo-consumer-text-bold">Unblock</p>
              </Button>
            </div>
          )}
          <div>
            <Form.Label className="bo-consumer-input-label">Plaid Item Id</Form.Label>
            <p className="bo-consumer-text text-truncate">{consumerPlaidItemId || '----'}</p>
            <Form.Label className="bo-consumer-input-label">Institution Id</Form.Label>
            <p className="bo-consumer-text">{institution?.id || '----'}</p>
            <Form.Label className="bo-consumer-input-label">Institution Name</Form.Label>
            <p className="bo-consumer-text">{institution?.name || '----'}</p>
          </div>
        </div>
        {bankAccounts && bankAccounts.length > 0 && (
          <div>
            <hr className="my-3 mb-2" />
            <p className="bo-consumer-text-bold">Accounts</p>
            <table className="w-100">
              <colgroup>
                <col width="21%" />
                <col width="21%" />
                <col width="21%" />
                <col width="4%" />
                <col width="12%" />
                <col width="21%" />
              </colgroup>
              <tbody>
                {bankAccounts.map(bankAccount => (
                  <tr key={bankAccount.plaidAccountId} className="align-bottom">
                    <td className="bo-consumer-text text-truncate bo-max-w-1">
                      {bankAccount.name}
                    </td>
                    <td className="bo-consumer-text ps-2 text-truncate bo-max-w-1">
                      {bankAccount.subType}
                    </td>
                    <td className="bo-consumer-text ps-2 text-nowrap">
                      {`****${bankAccount.mask}`}
                    </td>
                    <td className="ps-2 text-nowrap">
                      {bankAccount.plaidDwollaProcessorToken && (
                        <img src={dwollaLogo} alt="dwolla" width="auto" height="16" />
                      )}
                    </td>
                    <td className="ps-2 text-nowrap text-center">
                      {bankAccount.btTokenId && (
                        <img src={tabapayLogo} alt="tabapay" width="auto" height="16" />
                      )}
                    </td>
                    <td className="text-nowrap ps-2">
                      {bankAccount.isMain ? (
                        <Badge bg="success" className="bo-consumer-inline-badge py-1">
                          Main
                        </Badge>
                      ) : (
                        <Button
                          className="bo-common-inline-button p-0 m-0"
                          onClick={() => setAsMain(bankAccount.plaidAccountId)}
                        >
                          Set Main
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {bankAccountOwners && bankAccountOwners.length > 0 && (
          <div>
            <hr className="my-3 mb-2" />
            <p className="bo-consumer-text-bold">Main Account Owners</p>
            {bankAccountOwners.map(owner => (
              <div key={owner.emails && owner.emails[0]}>
                <Form.Label className="bo-consumer-input-label">Names</Form.Label>
                {owner?.names?.length > 0 ? (
                  owner.names.map(name => (
                    <p className="bo-consumer-text" key={name}>
                      {name}
                    </p>
                  ))
                ) : (
                  <p className="bo-consumer-text">---</p>
                )}
                <Form.Label className="bo-consumer-input-label">Addresses</Form.Label>
                {owner?.addresses?.length > 0 ? (
                  owner.addresses.map(item => {
                    const address = `${item.data.street}, ${item.data.city} ${item.data.region}, \
                                     ${item.data.postal_code}, ${item.data.country}`;

                    return (
                      <p className="bo-consumer-text text-truncate" key={address}>
                        {address}
                      </p>
                    );
                  })
                ) : (
                  <p className="bo-consumer-text">---</p>
                )}
                <Form.Label className="bo-consumer-input-label">Emails</Form.Label>
                {owner?.emails?.length > 0 ? (
                  owner.emails.map(email => (
                    <div className="d-flex" key={email.data}>
                      <p className="bo-consumer-text-bold bo-min-w-100 text-capitalize">
                        {email.type}
                      </p>
                      <p className="bo-consumer-text text-truncate">{email.data}</p>
                    </div>
                  ))
                ) : (
                  <p className="bo-consumer-text">---</p>
                )}
                <Form.Label className="bo-consumer-input-label">Phone Numbers</Form.Label>
                {owner?.phone_numbers?.length > 0 ? (
                  owner.phone_numbers.map(number => (
                    <div className="d-flex" key={number.data}>
                      <p className="bo-consumer-text-bold bo-min-w-100 text-capitalize">
                        {number.type}
                      </p>
                      <p className="bo-consumer-text text-truncate">{number.data}</p>
                    </div>
                  ))
                ) : (
                  <p className="bo-consumer-text">---</p>
                )}
              </div>
            ))}
          </div>
        )}
      </Spinner>
    </div>
  );
}

export default ConsumerBankAccountsSection;
